import { ArrowsTriangle } from '@timelyapp/icons/solid-icons';
import { MemoryConfigSchema } from '@timelyapp/integration-cloud-api-spec';
import type { DropdownMenuItem } from '@timelyapp/react';
import { useWithDispatch } from 'hooks/useWithDispatch';
import React, { useCallback, useMemo } from 'react';
import { ticApi } from 'redux/modules/tic/api-slice/tic.apiSlice';
import { saveMemoryConfig } from 'redux/modules/tic/tic.memories.actions';

/**
 * Hook for various reset options wrt memory filters and tool configs.
 */
export const useMemoryPreferencesResetOptions = () => {
  const actions = useWithDispatch({ saveMemoryConfig });

  const [resetAllToolConfigs] = ticApi.useResetAllToolConfigsMutation();

  const handleReset = useCallback(
    (scope: 'memory_config' | 'tool_configs' | 'everything') => {
      if (['everything', 'memory_config'].includes(scope)) {
        const defaults = MemoryConfigSchema.parse({});
        actions.saveMemoryConfig(defaults);
      }

      if (['everything', 'tool_configs'].includes(scope)) {
        resetAllToolConfigs();
      }
    },

    [actions, resetAllToolConfigs]
  );

  // Create memory display dropdown items
  const dropdownItems = useMemo(() => {
    const items: DropdownMenuItem[] = [];

    items.push({
      type: 'subMenu',
      label: 'Reset',
      variant: 'alert',
      icon: <ArrowsTriangle />,
      subOptions: [
        {
          type: 'label',
          label: 'Choose what to reset',
        },
        {
          type: 'item',
          label: 'Display options',
          closeOnSelect: true,
          icon: <ArrowsTriangle />,
          onSelect: () => {
            handleReset('memory_config');
          },
        },
        {
          type: 'item',
          label: 'App & website customizations',
          closeOnSelect: true,
          icon: <ArrowsTriangle />,
          onSelect: () => {
            handleReset('tool_configs');
          },
        },
        {
          type: 'item',
          label: 'Reset everything',
          closeOnSelect: true,
          icon: <ArrowsTriangle />,
          onSelect: () => {
            handleReset('everything');
          },
        },
        { type: 'separator' },
        {
          type: 'message',
          icon: 'auto',
          description:
            'Reset brings you back to recommended settings. You can always customize things again.',

          variant: 'info',
        },
      ],
    });

    return items;
  }, [handleReset]);

  return {
    dropdownItems,
  };
};

export default useMemoryPreferencesResetOptions;
